import * as PXBColors from '@brightlayer-ui/colors';
import * as PXBCore from '@brightlayer-ui/react-components/core';
import * as MuiCore from '@mui/material';
import { Checkbox, FormControlLabel } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useManageDeviceProfileChannels } from '../../data/DataProviders/DeviceProfileChannelsProvider';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { DerivedChannelData, isDataLoaded } from '../../types';
import { DataIndicators } from '../DataIndicators';

const useStyles = makeStyles({
    derivedBadge: {
        fontSize: 10,
        width: '60px',
        height: '16px',
        color: PXBColors.blue[500],
        backgroundColor: PXBColors.blue[100],
        fontWeight: 700,
        lineHeight: '16px',
        letterSpacing: 1,
        textAlign: 'center',
    },
});

export const ChannelList: React.FC<{
    existingChannelsData: Function;
    derivedChannelsData: Function;
    isCheckAll: boolean;
    derivedChannels: DerivedChannelData[];
}> = (props) => {
    const channelsData = useManageDeviceProfileChannels();
    const [channelList, setChannelList] = useState<any[]>([]);
    const [derivedChannelList, setDerivedChannelList] = useState<any[]>([]);
    const styles = useStyles();
    const [checked, setChecked] = useState<any[]>([]);
    const { t } = useLanguageLocale();

    useEffect(() => {
        if (channelList.length > 0) {
            setChannelList(channelList.map((channel) => ({ ...channel, checked: props.isCheckAll })));
            const checkedItems = channelList.map((item) => item.tagId);

            const derivedCheckedItems = derivedChannelList.map((channel) => ({
                ...channel,
                checked: props.isCheckAll,
            }));
            setDerivedChannelList(derivedCheckedItems);

            if (props.isCheckAll) {
                props.existingChannelsData(checkedItems);
                props.derivedChannelsData(derivedCheckedItems);
            } else {
                setChecked([]);
                props.existingChannelsData([]);
                props.derivedChannelsData(derivedCheckedItems);
            }
        }
    }, [props.isCheckAll]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setDerivedChannelList(props.derivedChannels);
    }, [props.derivedChannels]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isDataLoaded(channelsData)) {
            // post loading all the channels from the profile, mark every channel as selected if checkAll is true and update the channelList
            channelsData.value.forEach((item) => {
                item.checked = props.isCheckAll;
            });

            setChannelList(channelsData.value);

            // keep a list of selected tag ids
            const checkedItems = channelsData.value.map((item) => item.tagId);
            setChecked(checkedItems);

            // on load, every channel is selected
            props.existingChannelsData(checkedItems);
        }
    }, [channelsData]); //eslint-disable-line react-hooks/exhaustive-deps

    const onCheckChange = (event: any, tagId: string, index: number): void => {
        let updatedList = [...checked];
        if (event.target.checked) {
            updatedList = [...checked, tagId];
        } else {
            updatedList.splice(checked.indexOf(tagId), 1);
        }
        setChecked(updatedList);
        props.existingChannelsData(updatedList);

        setChannelList(
            channelList.map((channel, currentIndex) =>
                currentIndex === index ? { ...channel, checked: !channel.checked } : channel
            )
        );
    };

    const onDerivedCheckChange = (index: number): void => {
        const items = derivedChannelList.map((channel, currentIndex) =>
            currentIndex === index ? { ...channel, checked: !channel.checked } : channel
        );
        setDerivedChannelList(items);
        props.derivedChannelsData(items);
    };

    return (
        <>
            {isDataLoaded(channelsData) && channelList.length > 0 && (
                <div
                    data-testid="device-list-item"
                    style={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                >
                    {derivedChannelList.map((deriveChannel, index) => (
                        <div data-testid="device-list-item" key={index}>
                            <PXBCore.InfoListItem
                                title=""
                                hidePadding={true}
                                backgroundColor={PXBColors.white[50]}
                                divider={'full'}
                                subtitleSeparator="<"
                                iconColor={PXBColors.gray[500]}
                                // onClick={(): void =>
                                // }
                                leftComponent={
                                    <div>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={deriveChannel.checked}
                                                    data-testid="hi-critical"
                                                    value={deriveChannel}
                                                    onChange={(): void => onDerivedCheckChange(index)}
                                                />
                                            }
                                            label=""
                                        />

                                        <MuiCore.Typography
                                            variant="subtitle1"
                                            display="inline"
                                            style={{ lineHeight: 0.25 }}
                                        >
                                            {deriveChannel.name}
                                            <MuiCore.Typography
                                                variant="body2"
                                                display="block"
                                                style={{ marginLeft: 45, lineHeight: 0 }}
                                            >
                                                {deriveChannel.dataType}
                                            </MuiCore.Typography>
                                        </MuiCore.Typography>
                                    </div>
                                }
                                rightComponent={
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <MuiCore.Typography
                                            variant="body2"
                                            display="inline"
                                            className={styles.derivedBadge}
                                        >
                                            {t('MANAGEDEVICEPROFILES.DERIVED_BADGE')}
                                        </MuiCore.Typography>
                                        <MuiCore.IconButton style={{ marginLeft: 16 }}></MuiCore.IconButton>
                                    </div>
                                }
                            />
                        </div>
                    ))}
                    {channelList.map((channel, index) => (
                        <div data-testid="device-list-item" key={index}>
                            <PXBCore.InfoListItem
                                title=""
                                hidePadding={true}
                                backgroundColor={PXBColors.white[50]}
                                // divider={channelList.length - 1 ? 'full' : undefined}
                                subtitleSeparator="<"
                                iconColor={PXBColors.gray[500]}
                                // onClick={(): void =>
                                // }
                                leftComponent={
                                    <div>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={channel.checked}
                                                    data-testid="channel-val"
                                                    value={channel}
                                                    onChange={(evt): void => onCheckChange(evt, channel.tagId, index)}
                                                />
                                            }
                                            label=""
                                        />

                                        <MuiCore.Typography
                                            variant="subtitle1"
                                            display="inline"
                                            style={{ lineHeight: 0.25 }}
                                        >
                                            {channel.name}
                                            <MuiCore.Typography
                                                variant="body2"
                                                display="block"
                                                style={{ marginLeft: 45 }}
                                            >
                                                {channel.channelType}
                                            </MuiCore.Typography>
                                        </MuiCore.Typography>
                                    </div>
                                }
                                rightComponent={
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        {channel.isDerived && (
                                            <MuiCore.Typography
                                                variant="body2"
                                                display="inline"
                                                className={styles.derivedBadge}
                                            >
                                                {t('MANAGEDEVICEPROFILES.DERIVED_BADGE')}
                                            </MuiCore.Typography>
                                        )}
                                        <MuiCore.IconButton style={{ marginLeft: 16 }}></MuiCore.IconButton>
                                    </div>
                                }
                            />
                        </div>
                    ))}
                </div>
            )}
            {!isDataLoaded(channelsData) && (
                <DataIndicators
                    errorMessage="Channels not found"
                    emptyMessage="Channels not found"
                    inline
                    data={channelsData}
                    emptyIcon="Devices"
                    isEmpty={false}
                    isDetailedInfo={false}
                />
            )}
        </>
    );
};
